import { useState } from 'react'

import { Edit02Icon, Upload04Icon } from '@hugeicons/react'
import _ from 'lodash'
import type { UseFormReturn } from 'react-hook-form'
import { FormProvider } from 'react-hook-form'

import { AppIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { FormControl } from '@/shared/ui/Form'

import { MissionFileEvidenceControls } from './MissionFileEvidenceControls'
import { MissionImageEvidenceControls } from './MissionImageEvidenceControls'
import { MissionLinkEvidenceControls } from './MissionLinkEvidenceControls'
import { MissionTextEvidenceControls } from './MissionTextEvidenceControls'
import type { MissionWithProgress } from '@/types/mission'

import { MissionQrCodeEvidenceControls } from './MissionQrCodeEvidenceControls'

import type { MissionShowStatus } from '../../constants/showStatus'
import { missionShowCardBodyClass, missionShowCardClass } from './showCard'
import { MissionSubmittedEvidenceBlock } from './MissionSubmittedEvidenceBlock'

type EvidenceFormValues = {
  content: string
  media_id?: number
}

type MissionEvidenceSectionProps = {
  mission: MissionWithProgress
  showStatus: MissionShowStatus
  submission?: MissionWithProgress['_progress']['submission']
  form: UseFormReturn<EvidenceFormValues>
  isSubmitting: boolean
  isReadonly: boolean
  showEvidenceForm: boolean
  showAutoComplete: boolean
  canResubmit: boolean
  isCapacityFull: boolean
  onSubmit: (values: EvidenceFormValues) => void
  onOpenCamera: () => void
}

const EvidenceForm = ({
  mission,
  form,
  isSubmitting,
  isReadonly,
  isCapacityFull,
  submitLabel,
  onSubmit,
  onOpenCamera
}: {
  mission: MissionWithProgress
  form: UseFormReturn<EvidenceFormValues>
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  submitLabel: string
  onSubmit: (values: EvidenceFormValues) => void
  onOpenCamera: () => void
}) => {
  const evidenceKind = _.get(mission, 'evidence_kind', 'text')

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        {evidenceKind === 'link' && (
          <MissionLinkEvidenceControls
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            submitLabel={submitLabel}
          />
        )}

        {evidenceKind === 'text' && (
          <MissionTextEvidenceControls
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            submitLabel={submitLabel}
          />
        )}

        {evidenceKind === 'image' && (
          <MissionImageEvidenceControls
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            submitLabel={submitLabel}
          />
        )}

        {evidenceKind === 'file' && (
          <MissionFileEvidenceControls
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            submitLabel={submitLabel}
          />
        )}

        {evidenceKind === 'qr_code' ? (
          <MissionQrCodeEvidenceControls
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            onOpenCamera={onOpenCamera}
          />
        ) : !['image', 'link', 'text', 'file'].includes(evidenceKind) ? (
          <Button
            type="submit"
            variant="primary"
            size="md"
            showTrailingIcon={false}
            className="w-full justify-center"
            disabled={isSubmitting || isCapacityFull || isReadonly}
            leadingIcon={
              isSubmitting ? (
                <AppIcon name="spinner-third" />
              ) : (
                <Upload04Icon size={16} variant="stroke" />
              )
            }
          >
            {isSubmitting ? 'Enviando...' : submitLabel}
          </Button>
        ) : null}

        <p className="m-0 text-center text-2xs text-gray-500">
          Após o envio, não será possível editar até a avaliação da equipe.
        </p>
      </form>
    </FormProvider>
  )
}

export const MissionEvidenceSection = ({
  mission,
  showStatus,
  submission,
  form,
  isSubmitting,
  isReadonly,
  showEvidenceForm,
  showAutoComplete,
  canResubmit,
  isCapacityFull,
  onSubmit,
  onOpenCamera
}: MissionEvidenceSectionProps) => {
  const [resubmitOpen, setResubmitOpen] = useState(false)

  const showSubmittedBlock =
    !!submission &&
    (showStatus === 'pending' ||
      showStatus === 'approved' ||
      showStatus === 'refused')

  const showForm =
    (showEvidenceForm && showStatus !== 'pending') ||
    (showStatus === 'pending' && canResubmit && resubmitOpen) ||
    (showStatus === 'refused' && showEvidenceForm)

  if (
    !mission.confirmation_required &&
    !showAutoComplete &&
    !showSubmittedBlock &&
    showStatus !== 'expired'
  ) {
    return null
  }

  return (
    <Card className={missionShowCardClass}>
      <Card.Body className={missionShowCardBodyClass}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Edit02Icon size={16} variant="duotone" className="text-primary" aria-hidden />
              <h2 className="m-0 text-sm font-bold text-gray-900">
                {showSubmittedBlock ? 'Sua evidência' : 'Sua evidência'}
              </h2>
            </div>
            {!showSubmittedBlock && !isReadonly && (
              <p className="m-0 mt-1 text-xs text-gray-600">
                Envie sua resposta para participar do desafio.
              </p>
            )}
            {showSubmittedBlock && showStatus !== 'expired' && (
              <p className="m-0 mt-1 text-xs text-gray-600">
                {showStatus === 'pending'
                  ? 'Acompanhe o status do seu envio abaixo.'
                  : showStatus === 'approved'
                    ? 'Sua evidência foi aprovada.'
                    : 'Veja o retorno da equipe e envie a correção.'}
              </p>
            )}
          </div>
          {isReadonly && showStatus === 'expired' && (
            <span className="shrink-0 text-caps font-bold uppercase tracking-wide text-gray-500">
              Missão encerrada
            </span>
          )}
        </div>

        {showStatus === 'expired' && (
          <p className="m-0 rounded-xl bg-gray-100 px-3.5 py-3 text-xs font-semibold text-gray-700">
            O prazo desta missão expirou. Não é mais possível enviar evidências.
          </p>
        )}

        {isCapacityFull && showStatus !== 'expired' && (
          <p className="m-0 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            Esta missão está lotada no momento. Aguarde novas vagas para
            participar.
          </p>
        )}

        {showSubmittedBlock && submission && (
          <MissionSubmittedEvidenceBlock
            mission={mission}
            submission={submission}
            showStatus={showStatus as 'pending' | 'approved' | 'refused'}
            canResubmit={canResubmit}
            resubmitOpen={resubmitOpen}
            onResubmit={() => setResubmitOpen(true)}
            onCancelResubmit={() => setResubmitOpen(false)}
          />
        )}

        {showForm && (
          <EvidenceForm
            mission={mission}
            form={form}
            isSubmitting={isSubmitting}
            isReadonly={isReadonly}
            isCapacityFull={isCapacityFull}
            submitLabel={
              showStatus === 'refused' || resubmitOpen
                ? 'Corrigir envio'
                : 'Enviar evidência'
            }
            onSubmit={values => {
              onSubmit(values)
              setResubmitOpen(false)
            }}
            onOpenCamera={onOpenCamera}
          />
        )}

        {showAutoComplete && showStatus !== 'expired' && (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormControl
                name="content"
                defaultValue="Completado sem necessidade de evidência"
                hidden
              />
              <Button
                type="submit"
                variant="primary"
                size="md"
                showTrailingIcon={false}
                className="w-full justify-center"
                disabled={isSubmitting || isCapacityFull}
                leadingIcon={
                  isSubmitting ? (
                    <AppIcon name="spinner-third" />
                  ) : (
                    <Upload04Icon size={16} variant="stroke" />
                  )
                }
              >
                {isSubmitting ? 'Concluindo...' : 'Concluir missão'}
              </Button>
            </form>
          </FormProvider>
        )}

        {showStatus === 'not_started' && (
          <p className="m-0 text-xs font-semibold text-gray-600">
            Esta missão estará disponível em breve. Volte quando o desafio
            abrir.
          </p>
        )}
      </Card.Body>
    </Card>
  )
}
